import React, { useEffect, useRef, useState } from 'react';
import { List, Title } from '@meitu/saas-ui';
import { MaterialCard } from '@meitu/saas-editor-ui/es/components/material-card';
import { useDragLayer } from '@/hooks/use-drag-layer';
import { useLocaleStore } from '@/i18n';
import { fetchImageMaterialTopiDetail, fetchMaterialSearchList, fetchNewMaterialList } from '@/services/material';
import { MaterialListItem } from '@/services/material/index.d';
import { checkShowSvipCorner, isInRecommendMaterialTest } from '@/utils/ab-test';
import { useSelectMaterial } from '../../hooks/use-select-material';
import { IColorItem } from '../../material-collapse';
import { TAG_TYPE } from '../../type';
import { mergePresetList } from './preset-list';

const { BackTitle } = Title;

interface ICategory {
    name?: string;
    id?: string | number;
}

interface IMaterialDetailList {
    onShowDetailsCollapse: (bool: boolean, name?: string, id?: number) => void;
    categoryInfo: ICategory | null;
    currentTag: TAG_TYPE | number;
    color: IColorItem | null;
    style?: React.CSSProperties;
}
// 每个分页请求多少个素材
const MATERIAL_SIZE = 50;

/**
 * 查询最新素材
 * @param cursor - 分页游标
 * @returns
 */
const queryNewMaterials = async (cursor: string | null) => {
    cursor ??= '';
    const { data } = await fetchNewMaterialList({
        count: MATERIAL_SIZE,
        cursor,
    });
    return {
        materials: data?.data?.materials,
        cursor: data?.data?.cursor,
    };
};

/**
 * 查询标签素材
 * @param cursor - 分页游标
 * @param id - 标签ID
 * @returns
 */
const queryMaterialsByCategory = async (cursor: string | null, id: string | number) => {
    cursor ??= '';
    const { data } = await fetchImageMaterialTopiDetail({
        cursor,
        count: MATERIAL_SIZE,
        category_id: id as number,
    });

    return {
        materials: data.response?.materials,
        cursor: data.response?.cursor,
    };
};
/**
 * 查询颜色素材
 * @param cursor - 分页游标
 * @param color - 颜色
 * @returns
 */
const queryColorMaterials = async (cursor: string | null, color: IColorItem | null) => {
    cursor ??= '';
    const { data } = await fetchMaterialSearchList({
        keyword: color?.name ?? '',
        cursor,
        count: MATERIAL_SIZE,
    });
    return {
        cursor: data.response?.cursor,
        materials:
            data.response?.materials?.map((item) => {
                return {
                    material_conf: item.material_conf,
                    name: item.name || '',
                    threshold: item.threshold || 0,
                    id: item.id || 0,
                    thumbnail: item.thumbnail || '',
                    corner_mark: item.corner_mark || '',
                    svg: item.svg,
                };
            }) || [],
    };
};

/**
 * 查询专题素材
 * @param cursor - 分页游标
 * @param id - 专题ID
 * @returns
 */
const queryTopicMaterials = async (cursor: string | null, id: string | number) => {
    const isInTest = isInRecommendMaterialTest();
    const params = isInTest ? { is_rec: 1 } : {};
    cursor ??= '';
    const { data } = await fetchImageMaterialTopiDetail({
        count: 10,
        category_id: id as number,
        cursor,
        ...params,
    });

    return {
        cursor: data.response?.cursor,
        materials: data.response?.materials,
    };
};

const MaterialDetailList = (props: IMaterialDetailList) => {
    const { $t } = useLocaleStore();
    const { onShowDetailsCollapse, categoryInfo, currentTag, color } = props;
    const { id, name } = categoryInfo ?? {};
    const [materialList, setMaterialList] = useState<MaterialListItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const selectMaterial = useSelectMaterial();
    const [currentCursor, setCurrentCursor] = useState<string | null>(null);
    const dragLayerHelper = useDragLayer();
    const isInit = useRef(false);

    const isTagFilter = typeof currentTag === 'number';
    const isColor = currentTag === TAG_TYPE.colors;

    const fetchMaterialList = async () => {
        try {
            if (!hasMore || loading) {
                return;
            }

            //setLoading(false) 统一到finally调用
            setLoading(true);

            let data;

            // 请求最新素材
            if (id === 'newMaterial') {
                data = await queryNewMaterials(currentCursor);
            }

            //请求标签素材
            if (isTagFilter) {
                if (!id) return;

                data = await queryMaterialsByCategory(currentCursor, id as number);
            }

            // 请求颜色素材
            if (isColor) {
                data = await queryColorMaterials(currentCursor, color);
            }

            // 默认请求专题素材
            if (!data && id) {
                data = await queryTopicMaterials(currentCursor, id as number);
            }

            if (data?.materials) {
                const { cursor, usedCursor = null, materials } = data as any;
                setMaterialList(materialList.concat(materials as MaterialListItem[]));
                if (usedCursor !== null) {
                    setHasMore(!!usedCursor);
                } else {
                    setCurrentCursor(cursor || '');
                    setHasMore(!!cursor);
                }
            } else {
                setHasMore(false);
            }
        } catch (e) {
            console.log('获取列表失败', e);
        } finally {
            setLoading(false);
        }
    };

    const handleCardClick = (data: MaterialListItem, byMousePosition = false) => {
        selectMaterial.append(data, byMousePosition);
    };

    useEffect(() => {
        setCurrentCursor(null);
        setHasMore(true);
        isInit.current = false;
        // 如果最近使用列表有数据，则直接设置为最近使用列表的数据
        // 判断是否还有还有下一屏，若没有，需要设置hasMore为false
        setMaterialList([]);
    }, [isTagFilter, color, id]);

    useEffect(() => {
        if (!materialList.length && hasMore) {
            fetchMaterialList();
            isInit.current = true;
        }
    }, [materialList, hasMore]);

    return (
        <div className="c-material-collapse-detail" style={{ ...props.style, width: 272, marginLeft: -6 }}>
            {!isTagFilter && !isColor ? (
                <div className="c-material-collapse-detail__title" style={{ marginLeft: 6 }}>
                    <BackTitle
                        text={name || $t((v) => v.collapse.materialCollapse.detailTitle)}
                        handleBackClick={() => {
                            onShowDetailsCollapse(false);
                        }}
                    />
                </div>
            ) : null}
            <List<MaterialListItem>
                bodyClassName="m-material-list"
                loading={loading}
                hasMore={hasMore}
                dataSource={mergePresetList(materialList, id)}
                nextPageRequest={fetchMaterialList}
                id="materialDetailList"
                renderItem={(data: MaterialListItem) => (
                    <div
                        key={data.id}
                        className="m-material-list-item"
                        draggable="true"
                        onMouseDown={(ev) =>
                            dragLayerHelper.init(
                                {
                                    naturalWidth: data.material_conf?.width,
                                    naturalHeight: data.material_conf?.height,
                                    callback: () => handleCardClick(data as MaterialListItem, true),
                                },
                                ev,
                            )
                        }
                        onMouseUp={() => {
                            dragLayerHelper.destroy();
                        }}
                    >
                        <MaterialCard
                            key={data.id}
                            width={78}
                            cardStyle={{ height: 78 }}
                            src={data.thumbnail}
                            cornerSrc={
                                checkShowSvipCorner()
                                    ? typeof data.corner_mark === 'string'
                                        ? data.corner_mark
                                        : ((data.corner_mark as any)?.url ?? '')
                                    : ''
                            }
                            cornerPlacement="topRight"
                            onClick={() => handleCardClick(data)}
                        />
                    </div>
                )}
            />
        </div>
    );
};

export default React.memo(MaterialDetailList);
